// =============================================================================
// components/IncomingCallHandler.tsx — root incoming 1:1 call ring handler.
//
// Bare-RN port of the incoming-call listener that lived in the Expo app's
// app/_layout.tsx (P3-5 CALLING group). It renders NOTHING — it is a top-level
// side-effect host mounted ABOVE the navigator in App.tsx (inside
// NavigationContainer), exactly like InAppNotification.
//
// MIGRATION (blueprint §2):
//   - expo-router useRouter().push({pathname:'/voice/call', params})
//       -> navigationRef.navigate('VoiceCall', { ...typed params }).
//         Lives above any screen, so it uses the imperative navigationRef
//         (not a useNavigation hook) — matching navigationRef.ts / Risk #3.
//   - Auth gating: the Expo handler keyed off the layout's (isReady && isAuthed).
//     Here useAuth() supplies state==='authed' so the socket listener registers
//     ONLY for a logged-in user (the socket carries their token + joins their
//     user:<id> room) and unregisters on logout.
//
// PRESERVED VERBATIM (LR8 call signaling — do NOT alter):
//   - the Accept/Decline Alert,
//   - Accept -> POST /voice/video/join/:callId mints a token -> push the
//     full-screen VoiceCall screen (isCaller:false),
//   - Decline / dismiss -> POST /voice/video/decline/:callId so the caller's
//     "Ringing…" resolves to "Declined" instead of hanging,
//   - the handlingCallRef double-tap / Alert+join race guard, crash-safe wrap.
//
// PARAM SHAPE: navigation/types.ts types isCaller as a boolean (not the Expo
// '0'/'1' string), so we pass `isCaller: false` here.
// =============================================================================
import { useEffect, useRef } from 'react';
import { Alert } from 'react-native';
import { useAuth } from '../navigation/useAuth';
import { navigate } from '../navigation/navigationRef';
import { api } from '../services/api';
import { onIncomingCall, type IncomingCallPayload } from '../services/socket';

export default function IncomingCallHandler() {
  const { state } = useAuth();
  const isAuthed = state === 'authed';

  // Guards against double-handling a single incoming call (Alert + join race).
  const handlingCallRef = useRef(false);

  // Incoming 1:1 video call listener. Registered ONCE we have a valid auth
  // session (so the socket carries the user's token and joins their `user:<id>`
  // room) and cleaned up on logout/unmount. On an inbound call we show a simple
  // Accept/Decline prompt: Accept -> POST /voice/video/join/:callId -> push the
  // full-screen call screen; Decline -> dismiss. Crash-safe + double-tap guarded.
  useEffect(() => {
    if (!isAuthed) return;

    const handler = (payload: IncomingCallPayload) => {
      try {
        const callId = payload?.callId;
        if (!callId) return;
        if (handlingCallRef.current) return; // already prompting/joining a call
        handlingCallRef.current = true;

        const caller =
          payload?.initiator?.displayName ||
          payload?.initiator?.username ||
          'Someone';
        const peerAvatar = payload?.initiator?.avatar || '';
        const media = payload?.callType === 'audio' ? 'audio' : 'video';

        // Signal a decline back to the caller so their "Ringing…" resolves to
        // "Declined" instead of hanging (LR8). Best-effort.
        const sendDecline = () => {
          api<any>(`/voice/video/decline/${callId}`, { method: 'POST' }).catch(() => {});
        };

        Alert.alert(
          media === 'audio' ? 'Incoming voice call' : 'Incoming video call',
          `${caller} is calling you`,
          [
            {
              text: 'Decline',
              style: 'cancel',
              onPress: () => { sendDecline(); handlingCallRef.current = false; },
            },
            {
              text: 'Accept',
              onPress: async () => {
                try {
                  const res = await api<any>(`/voice/video/join/${callId}`, {
                    method: 'POST',
                    body: JSON.stringify({
                      videoEnabled: media === 'video',
                      audioEnabled: true,
                    }),
                  });
                  const token = res.data?.token;
                  const url = res.data?.liveKitUrl || res.data?.url;
                  if (res.success && token && url) {
                    navigate('VoiceCall', {
                      callId,
                      token,
                      url,
                      media,
                      roomName: res.data?.roomName ?? payload?.roomName ?? '',
                      peerName: caller,
                      peerAvatar,
                      isCaller: false,
                    });
                  } else {
                    Alert.alert('Call ended', 'This call is no longer available.');
                  }
                } catch {
                  Alert.alert('Call failed', 'Could not join the call. Please try again.');
                } finally {
                  handlingCallRef.current = false;
                }
              },
            },
          ],
          { cancelable: true, onDismiss: () => { sendDecline(); handlingCallRef.current = false; } }
        );
      } catch {
        handlingCallRef.current = false;
      }
    };

    const unsubscribe = onIncomingCall(handler);
    return () => {
      unsubscribe();
    };
  }, [isAuthed]);

  // Side-effect-only host: renders nothing.
  return null;
}
